import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Character } from '../../data/characters';
import { StatusEffect } from '../../data/abilities';
import StatusEffectOverlay from './StatusEffectOverlay';

interface PlayerStatsProps {
  character: Character;
  health: number;
  energy: number;
  isPlayerTurn: boolean;
  isOpponent?: boolean;
  wins?: number;
  isWhitelisted?: boolean;
  defense: number;
  statusEffects: StatusEffect[];
  criticalMultiplier: number;
  dodgeRate: number;
  onTakeDamage?: (damage: number) => void;
}

export interface PlayerStatsRef {
  addFloatingNumber: (value: number, type: FloatingNumberType) => void;
}

export type FloatingNumberType = 'heal' | 'damage' | 'critical' | 'chain';

interface FloatingNumber {
  id: string;
  value: number;
  type: FloatingNumberType;
}

const getFloatingNumberColor = (type: FloatingNumberType) => {
  switch (type) {
    case 'heal':
      return 'text-green-500';
    case 'damage':
      return 'text-red-500';
    case 'critical':
      return 'text-yellow-500';
    case 'chain':
      return 'text-purple-500';
    default:
      return 'text-white';
  }
};

const PlayerStats = forwardRef<PlayerStatsRef, PlayerStatsProps>(({
  character,
  health,
  energy,
  isPlayerTurn,
  isOpponent,
  wins,
  isWhitelisted,
  defense,
  statusEffects,
  criticalMultiplier,
  dodgeRate,
  onTakeDamage
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [floatingNumbers, setFloatingNumbers] = useState<FloatingNumber[]>([]);

  useImperativeHandle(ref, () => ({
    addFloatingNumber: (value: number, type: FloatingNumberType) => {
      const newNumber: FloatingNumber = {
        id: Math.random().toString(),
        value,
        type
      };
      setFloatingNumbers(prev => [...prev, newNumber]);
      
      // Call onTakeDamage if provided and it's a damage type
      if (onTakeDamage && (type === 'damage' || type === 'critical' || type === 'chain')) {
        onTakeDamage(value);
      }
      
      setTimeout(() => {
        setFloatingNumbers(prev => prev.filter(n => n.id !== newNumber.id));
      }, 1000);
    }
  }));

  const healthPercentage = Math.max(0, Math.min(100, (health / 100) * 100));
  const energyPercentage = Math.max(0, Math.min(100, (energy / 100) * 100));

  return (
    <div ref={containerRef} className="relative p-4 bg-gray-800 rounded-lg mb-4">
      <div className={`relative ${isOpponent ? 'order-2' : 'order-1'}`}>
        {/* Character Info */}
        <div className="flex flex-col items-center mb-2">
          <h3 className="text-lg font-bold code-mono">{character.name}</h3>
          {wins !== undefined && (
            <span className="text-sm text-yellow-400 code-mono">Wins: {wins}</span>
          )}
          {isWhitelisted !== undefined && (
            <span className={`text-sm ${isWhitelisted ? 'text-green-400' : 'text-red-400'} code-mono`}>
              {isWhitelisted ? 'Whitelisted' : 'Not Whitelisted'}
            </span>
          )}
        </div>

        {/* Stats */}
        <div className="flex flex-col gap-2">
          {/* Health Bar */}
          <div className="flex items-center gap-2">
            <span className="text-sm code-mono">HP</span>
            <div className="flex-1 h-4 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-red-600 to-red-400 transition-all duration-300"
                style={{ width: `${(health / character.maxHealth) * 100}%` }}
              />
            </div>
            <span className="text-sm code-mono">{Math.max(0, health)}/{character.maxHealth}</span>
          </div>

          {/* Energy Bar */}
          <div className="flex items-center gap-2">
            <span className="text-sm code-mono">EP</span>
            <div className="flex-1 h-4 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-600 to-blue-400 transition-all duration-300"
                style={{ width: `${(energy / character.maxEnergy) * 100}%` }}
              />
            </div>
            <span className="text-sm code-mono">{energy}/{character.maxEnergy}</span>
          </div>

          {/* Defense */}
          {defense > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-blue-400 code-mono">Defense: {defense}</span>
            </div>
          )}

          {/* Critical and Dodge */}
          <div className="flex justify-between text-sm code-mono">
            <span className="text-yellow-400">Crit: {(criticalMultiplier * 100).toFixed(0)}%</span>
            <span className="text-blue-300">Dodge: {(dodgeRate * 100).toFixed(0)}%</span>
          </div>
        </div>

        {/* Status Effects */}
        {statusEffects.length > 0 && (
          <div className="mt-2">
            <StatusEffectOverlay statusEffects={statusEffects} />
          </div>
        )}

        {/* Floating Numbers */}
        <div className="absolute inset-0 pointer-events-none">
          {floatingNumbers.map((number) => (
            <div
              key={number.id}
              className={`absolute left-1/2 -translate-x-1/2 transform animate-float-up code-mono
                ${getFloatingNumberColor(number.type)} font-bold text-lg`}
            >
              {number.type === 'critical' && '⚡'}
              {number.type === 'chain' && '🔗'}
              {number.value}
            </div>
          ))}
        </div>
      </div>

      {/* Turn indicator */}
      {isPlayerTurn && (
        <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 
          bg-yellow-500 text-black px-2 py-1 rounded text-sm font-bold">
          Your Turn
        </div>
      )}
    </div>
  );
});

PlayerStats.displayName = 'PlayerStats';

export default PlayerStats; 